import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { FieldDefSchema, type FieldDef } from "#shared/schemas/fieldDef.ts";
import {
  FolderNodeSchema,
  FolderSchema,
  type Folder,
  type FolderNode,
} from "#shared/schemas/folder.ts";
import {
  LocalPlaylistPublishResultSchema,
  LocalPlaylistSchema,
  LocalPlaylistWithCountSchema,
  type LocalPlaylist,
  type LocalPlaylistWithCount,
} from "#shared/schemas/localPlaylist.ts";
import {
  PlaylistBrowseItemSchema,
  PlaylistSchema,
  type Playlist,
  type PlaylistBrowseItem,
} from "#shared/schemas/playlist.ts";
import {
  AlbumBrowseItemSchema,
  AlbumSchema,
  type AlbumBrowseItem,
} from "#shared/schemas/album.ts";
import { SavedViewSchema, type SavedView } from "#shared/schemas/savedView.ts";
import { AppStatusSchema, type AppStatus } from "#shared/schemas/appStatus.ts";
import { GraphMapSchema, type GraphMap } from "#shared/schemas/graphMap.ts";
import { TrackListSchema, TrackRowSchema, type TrackList } from "#shared/schemas/trackQuery.ts";
import { z } from "zod";
import { notifyError } from "@web/features/diagnostics/notifyError.ts";
import { api } from "@web/lib/api.ts";
import { coyLog } from "@web/lib/coyLog.ts";

const FieldsResponse = z.object({ fields: z.array(FieldDefSchema) });
const FoldersResponse = z.object({ folders: z.array(FolderNodeSchema) });
const LocalPlaylistsResponse = z.object({
  local_playlists: z.array(LocalPlaylistWithCountSchema),
});
const PlaylistsResponse = z.object({ playlists: z.array(PlaylistSchema) });
const PlaylistBrowseResponse = z.object({
  playlists: z.array(PlaylistBrowseItemSchema),
});
const AlbumBrowseResponse = z.object({
  albums: z.array(AlbumBrowseItemSchema),
});
const ViewsResponse = z.object({ views: z.array(SavedViewSchema) });
const LocalPlaylistCreated = z.object({ local_playlist: LocalPlaylistSchema });

export function useAppStatus(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: ["status"],
    queryFn: async (): Promise<AppStatus> => AppStatusSchema.parse(await api("/api/status")),
    refetchInterval: options?.refetchInterval,
  });
}

export function useFieldDefs() {
  return useQuery({
    queryKey: ["fields"],
    queryFn: async (): Promise<FieldDef[]> => {
      const body = FieldsResponse.parse(await api("/api/fields?include_archived=false"));
      return body.fields;
    },
  });
}

export function useFolders() {
  return useQuery({
    queryKey: ["folders"],
    queryFn: async (): Promise<FolderNode[]> => {
      const body = FoldersResponse.parse(await api("/api/folders"));
      return body.folders;
    },
  });
}

export function useLocalPlaylists() {
  return useQuery({
    queryKey: ["local-playlists"],
    queryFn: async (): Promise<LocalPlaylistWithCount[]> => {
      const body = LocalPlaylistsResponse.parse(await api("/api/local-playlists"));
      return body.local_playlists;
    },
  });
}

export function usePlaylists() {
  return useQuery({
    queryKey: ["playlists"],
    queryFn: async (): Promise<Playlist[]> => {
      const body = PlaylistsResponse.parse(await api("/api/playlists"));
      return body.playlists;
    },
  });
}

/** Browse → Playlists table (local derived counts, no Spotify). Optional `q` searches name + contents. */
export function usePlaylistBrowse(q = "") {
  const term = q.trim();
  return useQuery({
    queryKey: ["playlists", "browse", term],
    queryFn: async (): Promise<PlaylistBrowseItem[]> => {
      const sp = new URLSearchParams();
      if (term) sp.set("q", term);
      const qs = sp.toString();
      const body = PlaylistBrowseResponse.parse(
        await api(`/api/playlists/browse${qs ? `?${qs}` : ""}`),
      );
      return body.playlists;
    },
  });
}

export type { PlaylistBrowseItem };

/** Browse → Albums table from the local mirror. Optional `q` searches name / artist / track. */
export function useAlbumBrowse(q = "") {
  const term = q.trim();
  return useQuery({
    queryKey: ["albums", "browse", term],
    queryFn: async (): Promise<AlbumBrowseItem[]> => {
      const sp = new URLSearchParams();
      if (term) sp.set("q", term);
      const qs = sp.toString();
      const body = AlbumBrowseResponse.parse(
        await api(`/api/albums${qs ? `?${qs}` : ""}`),
      );
      return body.albums;
    },
  });
}

export type { AlbumBrowseItem };

export function useAlbum(id: string | null) {
  return useQuery({
    queryKey: ["albums", id],
    enabled: id !== null,
    queryFn: async () => {
      const body = z
        .object({ album: AlbumSchema })
        .parse(await api(`/api/albums/${encodeURIComponent(id!)}`));
      return body.album;
    },
  });
}

export function useHiddenPlaylists() {
  return useQuery({
    queryKey: ["playlists", "hidden"],
    queryFn: async (): Promise<Playlist[]> => {
      const body = PlaylistsResponse.parse(await api("/api/playlists/hidden"));
      return body.playlists;
    },
  });
}

export function useHidePlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/api/playlists/${id}/hide`, { method: "POST" }),
    onSuccess: () => {
      toast.success("Playlist hidden");
      void qc.invalidateQueries({ queryKey: ["playlists"] });
    },
    onError: (err) => failToast(err, "Failed to hide playlist"),
  });
}

export function useUnhidePlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/api/playlists/${id}/unhide`, { method: "POST" }),
    onSuccess: () => {
      toast.success("Playlist restored");
      void qc.invalidateQueries({ queryKey: ["playlists"] });
    },
    onError: (err) => failToast(err, "Failed to restore playlist"),
  });
}

export function useClonePlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name?: string;
      folder_id?: string | null;
    }): Promise<LocalPlaylist> => {
      const body = LocalPlaylistCreated.parse(
        await api(`/api/playlists/${input.id}/clone`, {
          method: "POST",
          json: { name: input.name, folder_id: input.folder_id },
        }),
      );
      return body.local_playlist;
    },
    onSuccess: () => {
      toast.success("Cloned to workspace");
      void qc.invalidateQueries({ queryKey: ["local-playlists"] });
    },
    onError: (err) => failToast(err, "Failed to clone playlist"),
  });
}

export function useSavedViews() {
  return useQuery({
    queryKey: ["views"],
    queryFn: async (): Promise<SavedView[]> => {
      const body = ViewsResponse.parse(await api("/api/views"));
      return body.views;
    },
  });
}

export function useCreateSavedView() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      query: SavedView["query"];
    }): Promise<SavedView> => {
      const body = z
        .object({ view: SavedViewSchema })
        .parse(
          await api("/api/views", {
            method: "POST",
            json: {
              name: input.name,
              entity_type: "track",
              query: input.query,
              layout: "table",
            },
          }),
        );
      return body.view;
    },
    onSuccess: (view) => {
      toast.success("View saved");
      void qc.invalidateQueries({ queryKey: ["views"] });
      void navigate(`/view/${encodeURIComponent(view.id)}`);
    },
    onError: (err) => failToast(err, "Failed to save view"),
  });
}

export function useRenameSavedView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      api(`/api/views/${input.id}`, { method: "PATCH", json: { name: input.name } }),
    onSuccess: () => {
      toast.success("View renamed");
      void qc.invalidateQueries({ queryKey: ["views"] });
    },
    onError: (err) => failToast(err, "Failed to rename view"),
  });
}

export function useDeleteSavedView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/api/views/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast.success("View deleted");
      void qc.invalidateQueries({ queryKey: ["views"] });
    },
    onError: (err) => failToast(err, "Failed to delete view"),
  });
}

function tracksQueryString(params: {
  scope: string;
  q?: string;
  sort?: string;
  cursor?: string;
  limit?: number;
  filter?: string;
}): string {
  const sp = new URLSearchParams();
  sp.set("scope", params.scope);
  if (params.q) sp.set("q", params.q);
  if (params.sort) sp.set("sort", params.sort);
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.filter) sp.set("filter", params.filter);
  sp.set("limit", String(params.limit ?? 200));
  return sp.toString();
}

/** Infinite cursor pages for the virtualized grid. */
export function useTracksInfinite(params: {
  scope: string;
  q?: string;
  sort?: string;
  limit?: number;
  filter?: string | null;
  /** When set, pages through a saved view instead of a bare scope. */
  viewId?: string;
}) {
  return useInfiniteQuery({
    queryKey: [
      "tracks-infinite",
      params.viewId ?? "",
      params.scope,
      params.q ?? "",
      params.sort ?? "",
      params.filter ?? "",
    ],
    queryFn: async ({ pageParam }): Promise<TrackList> => {
      const qs = tracksQueryString({
        scope: params.scope,
        q: params.q,
        sort: params.sort,
        cursor: pageParam ?? undefined,
        limit: params.limit ?? 200,
        filter: params.filter ?? undefined,
      });
      const path = params.viewId
        ? `/api/views/${params.viewId}/tracks?${qs}`
        : `/api/tracks?${qs}`;
      return TrackListSchema.parse(await api(path));
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });
}

function failToast(err: unknown, fallback: string) {
  notifyError(err, { fallbackCode: "SAVE_FAILED", actionLabel: fallback });
}

export function useConnectSpotify() {
  return useMutation({
    mutationFn: async () => {
      const body = z
        .object({ url: z.string().url() })
        .parse(
          await api("/api/spotify/connect", {
            method: "POST",
            json: { return_to: window.location.origin },
          }),
        );
      return body.url;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (err) => failToast(err, "Could not start Spotify connect"),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api("/api/spotify/disconnect", { method: "POST" }),
    onSuccess: () => {
      coyLog.warn("Disconnected Spotify — tokens cleared. Mirror data stays in SQLite until a sync replaces it.");
      toast.success("Disconnected");
      void qc.invalidateQueries({ queryKey: ["status"] });
    },
    onError: (err) => failToast(err, "Logout failed"),
  });
}

const SyncRunResponse = z.object({
  run: z
    .object({
      id: z.number(),
      status: z.string(),
      phase: z.string().nullable(),
      started_at: z.string(),
      finished_at: z.string().nullable(),
      api_calls: z.number(),
      error: z.string().nullable(),
      cursor: z
        .object({
          scope: z.enum(["liked", "library", "artists", "full"]).optional(),
          budget: z.number().optional(),
          liked_total: z.number().optional(),
          liked_done: z.number().optional(),
          artists_total: z.number().optional(),
          artists_done: z.number().optional(),
          playlists_listed: z.number().optional(),
          pending_playlists: z.array(z.string()).optional(),
          playlist_index: z.number().optional(),
          offset: z.number().optional(),
        })
        .nullable()
        .optional(),
      counts: z
        .object({
          tracks: z.number(),
          albums: z.number(),
          artists: z.number(),
          playlists: z.number(),
          liked: z.number(),
          playlist_tracks: z.number(),
        })
        .nullable()
        .optional(),
    })
    .nullable(),
  progress: z
    .object({
      scope: z.enum(["liked", "library", "full"]),
      phase: z.string().nullable(),
      label: z.string(),
      detail: z.string(),
      percent: z.number(),
      api_calls: z.number(),
      budget: z.number(),
    })
    .nullable(),
});

export type SyncScope = "liked" | "library" | "full";

export function useSyncRun(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: ["sync"],
    queryFn: async () => SyncRunResponse.parse(await api("/api/sync")),
    refetchInterval: options?.refetchInterval,
  });
}

const SyncPlanResponse = z.object({
  health: z.object({
    state: z.enum(["healthy", "throttled", "locked_out"]),
    label: z.string(),
    detail: z.string(),
    remaining_sec: z.number(),
    reason: z.enum(["RATE_LIMIT", "QUOTA_EXCEEDED"]).nullable(),
    calls_last_30s: z.number(),
    calls_today: z.number(),
    last_429: z
      .object({
        at: z.string(),
        path: z.string(),
        label: z.string().nullable(),
        retry_after_sec: z.number(),
        reason: z.enum(["RATE_LIMIT", "QUOTA_EXCEEDED"]),
        body_snippet: z.string().nullable(),
        recovered: z.boolean(),
      })
      .nullable(),
    caveat: z.string(),
  }),
  jobs: z.array(
    z.object({
      scope: z.enum(["liked", "library", "full"]),
      title: z.string(),
      work: z.string(),
      work_count: z.number(),
      cost_calls: z.number(),
      batch_size: z.number(),
      items_per_call: z.number(),
      time_sec: z.number(),
      result: z.string(),
      value_score: z.number(),
      budget_cap: z.number(),
      fits_budget: z.boolean(),
    }),
  ),
  max_cost: z.number(),
  page_size: z.number(),
  pace_ms: z.number(),
});

export function useSyncPlan(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: ["sync-plan"],
    queryFn: async () => SyncPlanResponse.parse(await api("/api/sync/plan")),
    refetchInterval: options?.refetchInterval ?? 10_000,
  });
}

export function useStartSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { scope: SyncScope }) => {
      coyLog.info(`Starting Spotify sync · scope=${input.scope}`);
      return z
        .object({
          run_id: z.number(),
          scope: z.enum(["liked", "library", "full"]),
          budget: z.number(),
        })
        .parse(await api("/api/sync", { method: "POST", json: input }));
    },
    onSuccess: (body) => {
      coyLog.info(`Sync accepted · run #${body.run_id} · ${body.scope} · budget ${body.budget}`);
      toast.success(`Sync started · ${body.scope} (#${body.run_id})`);
      void qc.invalidateQueries({ queryKey: ["sync"] });
      void qc.invalidateQueries({ queryKey: ["status"] });
    },
    onError: (err) => {
      coyLog.error("Sync failed to start", err instanceof Error ? err.message : err);
      failToast(err, "Could not start sync");
    },
  });
}

export function useCancelSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      coyLog.warn("Cancel sync requested");
      return z
        .object({ run_id: z.number().nullable() })
        .parse(await api("/api/sync", { method: "DELETE" }));
    },
    onSuccess: (body) => {
      toast.message(body.run_id != null ? `Cancelling sync #${body.run_id}` : "No sync running");
      void qc.invalidateQueries({ queryKey: ["sync"] });
      void qc.invalidateQueries({ queryKey: ["status"] });
    },
    onError: (err) => failToast(err, "Could not cancel sync"),
  });
}

const ArtistsResponse = z.object({
  artists: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
      image_url: z.string().nullable(),
      spotify_genres: z.array(z.string()).nullable().optional(),
      genre_ids: z.array(z.string()).optional().default([]),
      track_count: z.number(),
      album_count: z.number(),
      collab_total: z.number(),
      collabs: z.array(
        z.object({
          id: z.string(),
          name: z.string().nullable(),
          image_url: z.string().nullable(),
          shared_tracks: z.number(),
        }),
      ),
    }),
  ),
});

export type ArtistListItem = z.infer<typeof ArtistsResponse>["artists"][number];
export type ArtistCollabItem = ArtistListItem["collabs"][number];

export function useArtists() {
  return useQuery({
    queryKey: ["artists"],
    queryFn: async (): Promise<ArtistListItem[]> => {
      const body = ArtistsResponse.parse(await api("/api/artists"));
      return body.artists;
    },
  });
}

const CollabsResponse = z.object({
  collabs: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
      image_url: z.string().nullable(),
      shared_tracks: z.number(),
    }),
  ),
});

export function useArtistCollabs(artistId: string | null) {
  return useQuery({
    queryKey: ["artists", artistId, "collabs"],
    enabled: artistId !== null,
    queryFn: async () => {
      const body = CollabsResponse.parse(
        await api(`/api/artists/${encodeURIComponent(artistId!)}/collabs`),
      );
      return body.collabs;
    },
  });
}

const SharedTracksResponse = z.object({
  tracks: z.array(TrackRowSchema),
});

export function useSharedTracks(artistId: string | null, otherId: string | null) {
  return useQuery({
    queryKey: ["artists", artistId, "shared", otherId],
    enabled: artistId !== null && otherId !== null,
    queryFn: async () => {
      const body = SharedTracksResponse.parse(
        await api(
          `/api/artists/${encodeURIComponent(artistId!)}/shared/${encodeURIComponent(otherId!)}`,
        ),
      );
      return body.tracks;
    },
  });
}

export function useArtistTracks(artistId: string | null) {
  return useQuery({
    queryKey: ["artists", artistId, "tracks"],
    enabled: artistId !== null,
    queryFn: async () => {
      const body = SharedTracksResponse.parse(
        await api(`/api/artists/${encodeURIComponent(artistId!)}/tracks`),
      );
      return body.tracks;
    },
  });
}

export function useAlbumTracks(albumId: string | null) {
  return useQuery({
    queryKey: ["albums", albumId, "tracks"],
    enabled: albumId !== null,
    queryFn: async () => {
      const body = SharedTracksResponse.parse(
        await api(`/api/albums/${encodeURIComponent(albumId!)}/tracks`),
      );
      return body.tracks;
    },
  });
}

const ArtistAlbumsResponse = z.object({
  albums: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
      cover_url: z.string().nullable(),
      release_date: z.string().nullable(),
      track_count: z.number(),
    }),
  ),
});

export function useArtistAlbums(artistId: string | null) {
  return useQuery({
    queryKey: ["artists", artistId, "albums"],
    enabled: artistId !== null,
    queryFn: async () => {
      const body = ArtistAlbumsResponse.parse(
        await api(`/api/artists/${encodeURIComponent(artistId!)}/albums`),
      );
      return body.albums;
    },
  });
}

const GraphEgoSchemaClient = z.object({
  centre: z.object({
    type: z.enum(["artist", "album", "track"]),
    id: z.string(),
    name: z.string().nullable(),
    image_url: z.string().nullable(),
    track_count: z.number().optional(),
  }),
  ring: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
      image_url: z.string().nullable(),
      track_count: z.number(),
      genre_id: z.string().nullable().optional(),
      genre_name: z.string().nullable().optional(),
      genre_color: z.string().nullable().optional(),
      genre_source: z.enum(["user", "spotify"]).nullable().optional(),
      edge: z.object({
        neighbour_id: z.string(),
        via_track_ids: z.array(z.string()),
        via_album_ids: z.array(z.string()),
        shared_count: z.number(),
        weight: z.number(),
        track_shared: z.number(),
        album_shared: z.number(),
      }),
    }),
  ),
  total_neighbours: z.number(),
  ghosts: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
      image_url: z.string().nullable(),
      parent_id: z.string(),
      weight: z.number(),
    }),
  ),
  genre_source_in_play: z.enum(["user", "spotify", "mixed", "none"]),
  cluster: z
    .object({
      fully_connected: z.boolean(),
      artist_count: z.number(),
      shared_track_count: z.number(),
    })
    .nullable()
    .optional(),
});

export type GraphEgoClient = z.infer<typeof GraphEgoSchemaClient>;
export type GraphRingMemberClient = GraphEgoClient["ring"][number];

export function useGraphMap(opts?: { includeOrphans?: boolean }) {
  const includeOrphans = opts?.includeOrphans ?? false;
  return useQuery({
    queryKey: ["graph-map", includeOrphans],
    queryFn: async (): Promise<GraphMap> => {
      const sp = new URLSearchParams();
      if (includeOrphans) sp.set("orphans", "1");
      const qs = sp.toString();
      return GraphMapSchema.parse(
        await api(`/api/graph/map${qs ? `?${qs}` : ""}`),
      );
    },
  });
}

export function useGraphEgo(input: {
  type: "artist" | "album" | "track" | null;
  id: string | null;
  limit?: number;
  genreId?: string | null;
  ghosts?: boolean;
}) {
  return useQuery({
    queryKey: [
      "graph-ego",
      input.type,
      input.id,
      input.limit ?? 15,
      input.genreId ?? "",
      input.ghosts !== false,
    ],
    enabled: input.type !== null && input.id !== null,
    queryFn: async (): Promise<GraphEgoClient> => {
      const sp = new URLSearchParams();
      sp.set("type", input.type!);
      sp.set("id", input.id!);
      sp.set("limit", String(input.limit ?? 15));
      if (input.genreId) sp.set("genre", input.genreId);
      if (input.ghosts === true) sp.set("ghosts", "1");
      else sp.set("ghosts", "0");
      return GraphEgoSchemaClient.parse(await api(`/api/graph/ego?${sp}`));
    },
  });
}

const GraphSharedSchema = z.object({
  tracks: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      cover_url: z.string().nullable(),
      duration_ms: z.number().nullable(),
      album_name: z.string().nullable().optional(),
      artist_names: z.array(z.string()),
    }),
  ),
  albums: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
      cover_url: z.string().nullable(),
      release_date: z.string().nullable(),
    }),
  ),
});

export type GraphSharedClient = z.infer<typeof GraphSharedSchema>;

export function useGraphShared(artistId: string | null, otherId: string | null) {
  return useQuery({
    queryKey: ["graph-shared", artistId, otherId],
    enabled: artistId !== null && otherId !== null,
    queryFn: async (): Promise<GraphSharedClient> => {
      return GraphSharedSchema.parse(
        await api(
          `/api/graph/shared/${encodeURIComponent(artistId!)}/${encodeURIComponent(otherId!)}`,
        ),
      );
    },
  });
}

const GraphPathsResponse = z.object({
  paths: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      hops: z.array(
        z.object({
          type: z.enum(["artist", "album", "track"]),
          id: z.string(),
          label: z.string(),
        }),
      ),
      track_ids: z.array(z.string()),
      created_at: z.string(),
      updated_at: z.string(),
    }),
  ),
});

export type GraphPathClient = z.infer<typeof GraphPathsResponse>["paths"][number];

export function useGraphPaths() {
  return useQuery({
    queryKey: ["graph-paths"],
    queryFn: async (): Promise<GraphPathClient[]> => {
      const body = GraphPathsResponse.parse(await api("/api/graph/paths"));
      return body.paths;
    },
  });
}

export function useCreateGraphPath() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      hops: GraphPathClient["hops"];
      track_ids?: string[];
    }) => {
      const body = z
        .object({ path: GraphPathsResponse.shape.paths.element })
        .parse(
          await api("/api/graph/paths", {
            method: "POST",
            json: {
              name: input.name,
              hops: input.hops,
              track_ids: input.track_ids ?? [],
            },
          }),
        );
      return body.path;
    },
    onSuccess: () => {
      toast.success("Path saved");
      void qc.invalidateQueries({ queryKey: ["graph-paths"] });
    },
    onError: (err) => failToast(err, "Failed to save path"),
  });
}

export function useDeleteGraphPath() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/api/graph/paths/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast.success("Path deleted");
      void qc.invalidateQueries({ queryKey: ["graph-paths"] });
    },
    onError: (err) => failToast(err, "Failed to delete path"),
  });
}

export function useGraphPathToPlaylist() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (input: { id: string; name?: string }) => {
      const body = z
        .object({ local_playlist: LocalPlaylistSchema })
        .parse(
          await api(`/api/graph/paths/${input.id}/playlist`, {
            method: "POST",
            json: { name: input.name },
          }),
        );
      return body.local_playlist;
    },
    onSuccess: (playlist) => {
      toast.success("Playlist created from path", {
        action: {
          label: "Open",
          onClick: () => {
            void navigate(`/local/${encodeURIComponent(playlist.id)}`);
          },
        },
      });
      void qc.invalidateQueries({ queryKey: ["local-playlists"] });
    },
    onError: (err) => failToast(err, "Failed to create playlist"),
  });
}

const GenresFlatResponse = z.object({
  genres: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
      parent_id: z.string().nullable(),
      color: z.string().nullable(),
      position: z.number(),
      usage_count: z.number(),
    }),
  ),
});

export type GenreListItem = z.infer<typeof GenresFlatResponse>["genres"][number];

export function useGenresFlat() {
  return useQuery({
    queryKey: ["genres", "flat"],
    queryFn: async (): Promise<GenreListItem[]> => {
      const body = GenresFlatResponse.parse(await api("/api/genres/flat"));
      return body.genres;
    },
  });
}

const GenreCreated = z.object({
  genre: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    color: z.string().nullable(),
  }),
});

export function useCreateGenre() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      name: string,
    ): Promise<{ id: string; name: string; slug: string; color: string | null }> => {
      const parsed = GenreCreated.parse(await api("/api/genres", { method: "POST", json: { name } }));
      return parsed.genre;
    },
    onSuccess: () => {
      toast.success("Genre created");
      void qc.invalidateQueries({ queryKey: ["genres"] });
    },
    onError: (err) => failToast(err, "Failed to create genre"),
  });
}

export function useRenameGenre() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      api(`/api/genres/${input.id}`, { method: "PATCH", json: { name: input.name } }),
    onSuccess: () => {
      toast.success("Genre renamed");
      void qc.invalidateQueries({ queryKey: ["genres"] });
    },
    onError: (err) => failToast(err, "Failed to rename genre"),
  });
}

export function useDeleteGenre() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      mode?: "reassign_parent" | "promote" | "absorb" | "reassign";
      reassign_to?: string;
    }) => {
      const params = new URLSearchParams();
      if (input.mode) params.set("mode", input.mode);
      if (input.reassign_to) params.set("reassign_to", input.reassign_to);
      const q = params.toString();
      return api(`/api/genres/${input.id}${q ? `?${q}` : ""}`, { method: "DELETE" });
    },
    onSuccess: () => {
      toast.success("Genre deleted");
      void qc.invalidateQueries({ queryKey: ["genres"] });
      void qc.invalidateQueries({ queryKey: ["undo"] });
      void qc.invalidateQueries({ queryKey: ["entity-detail"] });
    },
    onError: (err) => failToast(err, "Failed to delete genre"),
  });
}

export function useMergeGenre() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { fromId: string; intoId: string }) =>
      api(`/api/genres/${input.fromId}/merge`, {
        method: "POST",
        json: { into_id: input.intoId },
      }),
    onSuccess: () => {
      toast.success("Genres merged");
      void qc.invalidateQueries({ queryKey: ["genres"] });
      void qc.invalidateQueries({ queryKey: ["undo"] });
      void qc.invalidateQueries({ queryKey: ["entity-detail"] });
      void qc.invalidateQueries({ queryKey: ["tracks-infinite"] });
    },
    onError: (err) => failToast(err, "Failed to merge genres"),
  });
}

export function useMoveGenre() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      parent_id: string | null;
      after_id?: string | null;
      before_id?: string | null;
    }) =>
      api(`/api/genres/${input.id}`, {
        method: "PATCH",
        json: {
          parent_id: input.parent_id,
          after_id: input.after_id,
          before_id: input.before_id,
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["genres"] });
    },
    onError: (err) => failToast(err, "Failed to move genre"),
  });
}

const FolderCreated = z.object({ folder: FolderSchema });

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: string | { name: string; parent_id?: string | null },
    ): Promise<Folder> => {
      const body = typeof input === "string" ? { name: input } : input;
      const parsed = FolderCreated.parse(await api("/api/folders", { method: "POST", json: body }));
      return parsed.folder;
    },
    onSuccess: () => {
      toast.success("Folder created");
      void qc.invalidateQueries({ queryKey: ["folders"] });
    },
    onError: (err) => failToast(err, "Failed to create folder"),
  });
}

export function useRenameFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      api(`/api/folders/${input.id}`, { method: "PATCH", json: { name: input.name } }),
    onSuccess: () => {
      toast.success("Folder renamed");
      void qc.invalidateQueries({ queryKey: ["folders"] });
    },
    onError: (err) => failToast(err, "Failed to rename folder"),
  });
}

export function useMoveFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      parent_id: string | null;
      after_id?: string | null;
      before_id?: string | null;
    }) =>
      api(`/api/folders/${input.id}`, {
        method: "PATCH",
        json: {
          parent_id: input.parent_id,
          after_id: input.after_id,
          before_id: input.before_id,
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["folders"] });
    },
    onError: (err) => failToast(err, "Failed to move folder"),
  });
}

function mapFolderCollapsed(nodes: FolderNode[], id: string, collapsed: boolean): FolderNode[] {
  return nodes.map((node) => {
    if (node.id === id) return { ...node, collapsed };
    if (node.children.length === 0) return node;
    return { ...node, children: mapFolderCollapsed(node.children, id, collapsed) };
  });
}

export function useSetFolderCollapsed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; collapsed: boolean }) =>
      api(`/api/folders/${input.id}`, {
        method: "PATCH",
        json: { collapsed: input.collapsed },
      }),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: ["folders"] });
      const previous = qc.getQueryData<FolderNode[]>(["folders"]);
      qc.setQueryData<FolderNode[]>(["folders"], (current) =>
        current ? mapFolderCollapsed(current, input.id, input.collapsed) : current,
      );
      return { previous };
    },
    onError: (err, _input, ctx) => {
      if (ctx?.previous) qc.setQueryData(["folders"], ctx.previous);
      failToast(err, "Failed to update folder");
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["folders"] });
    },
  });
}

export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/api/folders/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast.success("Folder deleted");
      void qc.invalidateQueries({ queryKey: ["folders"] });
      void qc.invalidateQueries({ queryKey: ["local-playlists"] });
    },
    onError: (err) => failToast(err, "Failed to delete folder"),
  });
}

export function useDuplicateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<Folder> => {
      const body = FolderCreated.parse(
        await api(`/api/folders/${id}/duplicate`, { method: "POST" }),
      );
      return body.folder;
    },
    onSuccess: () => {
      toast.success("Folder duplicated");
      void qc.invalidateQueries({ queryKey: ["folders"] });
      void qc.invalidateQueries({ queryKey: ["local-playlists"] });
    },
    onError: (err) => failToast(err, "Failed to duplicate folder"),
  });
}

export function useCreateLocalPlaylist() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      folder_id?: string | null;
      source_spotify_id?: string | null;
      track_ids?: string[];
    }): Promise<LocalPlaylist> => {
      const body = LocalPlaylistCreated.parse(
        await api("/api/local-playlists", {
          method: "POST",
          json: {
            name: input.name,
            folder_id: input.folder_id ?? null,
            source_spotify_id: input.source_spotify_id ?? null,
            track_ids: input.track_ids,
          },
        }),
      );
      return body.local_playlist;
    },
    onSuccess: (playlist) => {
      toast.success("Playlist created", {
        action: {
          label: "Open",
          onClick: () => {
            void navigate(`/local/${encodeURIComponent(playlist.id)}`);
          },
        },
      });
      void qc.invalidateQueries({ queryKey: ["local-playlists"] });
    },
    onError: (err) => failToast(err, "Failed to create playlist"),
  });
}

export function useRenameLocalPlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      api(`/api/local-playlists/${input.id}`, {
        method: "PATCH",
        json: { name: input.name },
      }),
    onSuccess: () => {
      toast.success("Playlist renamed");
      void qc.invalidateQueries({ queryKey: ["local-playlists"] });
    },
    onError: (err) => failToast(err, "Failed to rename playlist"),
  });
}

export function useMoveLocalPlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      folder_id: string | null;
      after_id?: string | null;
      before_id?: string | null;
    }) =>
      api(`/api/local-playlists/${input.id}`, {
        method: "PATCH",
        json: {
          folder_id: input.folder_id,
          after_id: input.after_id,
          before_id: input.before_id,
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["local-playlists"] });
    },
    onError: (err) => failToast(err, "Failed to move playlist"),
  });
}

export function useDeleteLocalPlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/api/local-playlists/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast.success("Playlist deleted");
      void qc.invalidateQueries({ queryKey: ["local-playlists"] });
    },
    onError: (err) => failToast(err, "Failed to delete playlist"),
  });
}

export function useDuplicateLocalPlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<LocalPlaylist> => {
      const body = LocalPlaylistCreated.parse(
        await api(`/api/local-playlists/${id}/duplicate`, { method: "POST" }),
      );
      return body.local_playlist;
    },
    onSuccess: () => {
      toast.success("Playlist duplicated");
      void qc.invalidateQueries({ queryKey: ["local-playlists"] });
    },
    onError: (err) => failToast(err, "Failed to duplicate playlist"),
  });
}

export function usePublishLocalPlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      LocalPlaylistPublishResultSchema.parse(
        await api(`/api/local-playlists/${encodeURIComponent(id)}/publish`, {
          method: "POST",
        }),
      ),
    onSuccess: (result) => {
      const n = result.track_count;
      toast.success(
        n === 0
          ? "Created an empty playlist on Spotify"
          : `Created on Spotify · ${n.toLocaleString()} track${n === 1 ? "" : "s"}`,
        {
          action: {
            label: "Open",
            onClick: () => {
              window.open(result.spotify_url, "_blank", "noopener,noreferrer");
            },
          },
        },
      );
      void qc.invalidateQueries({ queryKey: ["local-playlists"] });
      void qc.invalidateQueries({ queryKey: ["entity-detail", "local_playlist", result.local_playlist.id] });
    },
    onError: (err) => failToast(err, "Failed to create playlist on Spotify"),
  });
}
